import React, { useState, useRef } from 'react';
import './FileUpload.css';
import apiClient from '../../services/apiClient';

const FileUpload = ({ onFileUpload, acceptedTypes = "image/*,video/*", maxSize = 52428800, label = "Télécharger un fichier" }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      console.log('📡 Uploading files to backend...');

      const validFiles = Array.from(files).filter(file => {
          if (file.size > maxSize) {
            alert(`Le fichier ${file.name} est trop volumineux. Taille maximale: ${Math.round(maxSize / 1024 / 1024)}MB`);
            return false;
          }

          const isImage = file.type.startsWith('image/');
          const isVideo = file.type.startsWith('video/');
          
          if (!isImage && !isVideo) {
            alert(`Le fichier ${file.name} n'est pas un format supporté. Formats acceptés: images et vidéos.`);
            return false;
          }
          
          return true;
        });
        
        if (validFiles.length === 0) return;
        
        // Upload to backend
        const response = await apiClient.uploadFiles(validFiles);
        
        if (response.success && response.files) {
          // Process each uploaded file
          response.files.forEach(fileData => {
            if (!fileData.error) {
              onFileUpload(fileData);
              console.log(`✅ File uploaded: ${fileData.original_name}`);
            } else {
              console.error('Upload error:', fileData.error);
              alert(`Erreur: ${fileData.error}`);
            }
          });
          
      if (response.uploaded > 0) {
        alert(`✅ ${response.uploaded} fichier(s) téléchargé(s) avec succès!`);
      }
    } else {
      throw new Error(response.message || 'Upload failed');
    }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      console.log('💾 Falling back to local processing...');
      await processFilesLocally(files);
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Fallback: Process files locally (original method)
  const processFilesLocally = async (files) => {
    for (const file of files) {
      try {
        // Validate file size
        if (file.size > maxSize) {
          alert(`Le fichier ${file.name} est trop volumineux. Taille maximale: ${Math.round(maxSize / 1024 / 1024)}MB`);
          continue;
        }

        // Validate file type
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        
        if (!isImage && !isVideo) {
          alert(`Le fichier ${file.name} n'est pas un format supporté. Formats acceptés: images et vidéos.`);
          continue;
        }

        // Process file
        if (isImage) {
          await processImage(file);
        } else if (isVideo) {
          await processVideo(file);
        }
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        alert(`Erreur lors du traitement de ${file.name}`);
      }
    }
  };

  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to resize if needed
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Resize image if too large (max 800px width)
          const maxWidth = 800;
          const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
          
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;
          
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Convert to base64 with compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          
          const fileData = {
            type: 'image',
            name: file.name,
            data: compressedDataUrl,
            originalSize: file.size,
            size: compressedDataUrl.length,
            width: canvas.width,
            height: canvas.height,
            lastModified: file.lastModified
          };
          
          onFileUpload(fileData);
          resolve();
        };
        
        img.onerror = reject;
        img.src = e.target.result;
      };
      
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const processVideo = (file) => {
    return new Promise((resolve, reject) => {
      // For videos, we'll store metadata and create a preview
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const video = document.createElement('video');
        
        video.onloadedmetadata = () => {
          // Create canvas for thumbnail
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set thumbnail size
          const thumbWidth = 320;
          const thumbHeight = (video.videoHeight / video.videoWidth) * thumbWidth;
          
          canvas.width = thumbWidth;
          canvas.height = thumbHeight;
          
          // Seek to 1 second for thumbnail
          video.currentTime = 1;
        };
        
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const thumbWidth = 320;
          const thumbHeight = (video.videoHeight / video.videoWidth) * thumbWidth;
          
          canvas.width = thumbWidth;
          canvas.height = thumbHeight;
          
          ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight);
          const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
          
          // Check file size and warn user
          const fileSizeInMB = file.size / (1024 * 1024);
          const base64SizeInMB = (e.target.result.length * 0.75) / (1024 * 1024); // Base64 is ~33% larger
          
          console.log(`Video file size: ${fileSizeInMB.toFixed(2)} MB, Base64 size: ${base64SizeInMB.toFixed(2)} MB`);
          
          let videoData = e.target.result;
          let sizeWarning = '';
          
          // If video is too large, offer to store without data (thumbnail only)
          if (base64SizeInMB > 20) {
            const userConfirm = window.confirm(
              `⚠️ VIDÉO VOLUMINEUSE DÉTECTÉE\n\n` +
              `Taille: ${fileSizeInMB.toFixed(1)} MB\n` +
              `Durée: ${video.duration ? Math.round(video.duration) + 's' : 'inconnue'}\n\n` +
              `Cette vidéo sera affichée dans la galerie mais optimisée pour le stockage.\n` +
              `La miniature et les informations seront conservées.\n\n` +
              `Voulez-vous continuer l'upload ?`
            );
            
            if (!userConfirm) {
              reject(new Error('Upload annulé par l\'utilisateur'));
              return;
            }
            
            sizeWarning = `⚠️ Vidéo ${fileSizeInMB.toFixed(1)}MB - Sera optimisée`;
            // Still include data initially for preview, but mark as large
            videoData = e.target.result;
          }
          
          const fileData = {
            type: 'video',
            name: file.name,
            data: videoData, // For demo, store as base64. In production, upload to CDN
            thumbnail: thumbnail,
            size: file.size,
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight,
            lastModified: file.lastModified,
            isLarge: base64SizeInMB > 20,
            sizeWarning: sizeWarning
          };
          
          onFileUpload(fileData);
          resolve();
        };
        
        video.onerror = reject;
        video.src = e.target.result;
      };
      
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-upload-container">
      <div 
        className={`file-upload-zone ${isDragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <div className="upload-content">
          {uploading ? (
            <>
              <i className="fas fa-spinner fa-spin upload-icon"></i>
              <p>Téléchargement en cours...</p>
            </>
          ) : (
            <>
              <i className="fas fa-cloud-upload-alt upload-icon"></i>
              <p className="upload-text">
                <strong>Cliquez ici</strong> ou <strong>glissez-déposez</strong> vos fichiers
              </p>
              <p className="upload-hint">
                Images et vidéos supportées • Max {Math.round(maxSize / 1024 / 1024)}MB
              </p>
              {acceptedTypes.includes('video') && (
                <div className="video-storage-tip" style={{
                  marginTop: '0.5rem',
                  fontSize: '0.75rem',
                  color: '#f59e0b',
                  backgroundColor: '#fef3c7',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #fde68a',
                  textAlign: 'center'
                }}>
                  <i className="fas fa-info-circle"></i>
                  <span style={{ marginLeft: '4px' }}>
                    <strong>Conseil:</strong> Vidéos &gt;20MB optimisées pour stockage. Qualité HD recommandée (&lt;5min).
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
